// E8TableAverageClosedTask.js
// Zadanie zamkniete E8 (2025 CZE zad. 1) - srednia arytmetyczna z tabeli.
// Dane sa wartosci z 4 dni; srednia z 5 dni jest znana => 5. dzien = 5*avg - suma.

import React from "react";
import { BlockMath, ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];
const DAYS = ["wtorek", "środa", "czwartek", "piątek"];
const LAST_DAY = "sobota";

function randInt(min, max) {
  return min + Math.floor(Math.random() * (max - min + 1));
}

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildData({ pages, avg, fixedOptions = null }) {
  const sum = pages.reduce((acc, n) => acc + n, 0);
  const totalDays = pages.length + 1;
  const answer = avg * totalDays - sum;

  let options;
  let correctIndex;
  if (fixedOptions) {
    options = fixedOptions.map((value, index) => ({ value, label: LETTERS[index] }));
    correctIndex = fixedOptions.indexOf(answer);
  } else {
    const pool = [answer, answer - 10, answer + 10, answer + 20, answer - 20, avg];
    const unique = [];
    pool.forEach((v) => {
      if (v > 0 && !unique.includes(v)) unique.push(v);
    });
    const distractors = unique.filter((v) => v !== answer).slice(0, 3);
    const ordered = shuffle([answer, ...distractors]);
    options = ordered.map((value, index) => ({ value, label: LETTERS[index] }));
    correctIndex = ordered.indexOf(answer);
  }

  return { pages, avg, totalDays, sum, answer, options, correctIndex };
}

function createOriginalTask() {
  return {
    type: "E8TableAverageClosedTask",
    data: buildData({ pages: [55, 68, 72, 49], avg: 60, fixedOptions: [46, 56, 75, 76] }),
    source: { variant: "original", exam: "E8", session: "2025_CZE", taskNumber: 1 },
    tags: ["e8", "statystyka", "srednia-arytmetyczna", "tabela"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  let pages;
  let avg;
  let answer;
  do {
    avg = randInt(40, 80);
    pages = Array.from({ length: 4 }, () => randInt(avg - 18, avg + 18));
    answer = avg * 5 - pages.reduce((acc, n) => acc + n, 0);
  } while (answer <= 0 || answer > 120);

  return {
    type: "E8TableAverageClosedTask",
    data: buildData({ pages, avg }),
    source: { variant: "random", basedOn: "E8_2025_CZE_1" },
    tags: ["e8", "statystyka", "srednia-arytmetyczna", "tabela"],
  };
}

function Table({ data }) {
  const cell = { border: "1px solid currentColor", padding: "7px 10px", textAlign: "center", opacity: 0.92 };
  return (
    <table style={{ borderCollapse: "collapse", margin: "12px auto", minWidth: 420 }}>
      <tbody>
        <tr>
          <td style={{ ...cell, textAlign: "left" }}>Dzień tygodnia</td>
          {DAYS.map((d) => <td key={d} style={cell}>{d}</td>)}
        </tr>
        <tr>
          <td style={{ ...cell, textAlign: "left" }}>Liczba przeczytanych stron książki</td>
          {data.pages.map((p, i) => <td key={i} style={cell}>{p}</td>)}
        </tr>
      </tbody>
    </table>
  );
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Robert rozpoczął czytanie książki we wtorek i zakończył w sobotę. W tabeli
        podano liczbę stron, które Robert przeczytał w kolejnych dniach tygodnia od
        wtorku do piątku.
      </p>
      <Table data={data} />
      <p>
        Od wtorku do soboty włącznie Robert czytał dziennie średnio{" "}
        <InlineMath math={`${data.avg}`} /> stron tej książki.
      </p>
      <p>
        Ile stron tej książki Robert przeczytał w {LAST_DAY}? Wybierz właściwą
        odpowiedź spośród podanych.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8, marginTop: 10 }}>
        {data.options.map((option, index) => (
          <div key={option.value}>
            <ChoiceOptionMath letter={LETTERS[index]} math={`${option.value}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  return (
    <ABCDInput
      options={data.options.map((option, index) => ({ label: LETTERS[index], textNode: LETTERS[index] }))}
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer === data.correctIndex;
}

export function renderAnswer({ data }) {
  return <ChoiceOptionMath letter={LETTERS[data.correctIndex]} math={`${data.answer}`} />;
}

export function renderUserAnswer(userAnswer, { data }) {
  if (typeof userAnswer !== "number") return <i>(brak odpowiedzi)</i>;
  const option = data.options[userAnswer];
  if (!option) return <i>(nieprawidłowy wybór)</i>;
  return <ChoiceOptionMath letter={LETTERS[userAnswer]} math={`${option.value}`} />;
}

export function renderExplanation({ data }) {
  const { pages, avg, totalDays, sum, answer } = data;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p><strong>Krok 1.</strong> Suma stron z {totalDays} dni przy średniej <InlineMath math={`${avg}`} />:</p>
      <BlockMath math={`${avg}\\cdot ${totalDays}=${avg * totalDays}`} />
      <p><strong>Krok 2.</strong> Suma stron przeczytanych od wtorku do piątku:</p>
      <BlockMath math={`${pages.join("+")}=${sum}`} />
      <p><strong>Krok 3.</strong> W sobotę Robert przeczytał:</p>
      <BlockMath math={`${avg * totalDays}-${sum}=${answer}`} />
      <p>Poprawna odpowiedź to <InlineMath math={`\\text{${LETTERS[data.correctIndex]}}`} />.</p>
    </div>
  );
}
